import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { addDays, datePart, formatShortDate, formatWeekday, timePart } from "../format";
import type { Schemas } from "../api/client";
import { schoolShortTitle, timetableChange, timetableClasses } from "../timetablePresentation";
import { agendaPeriod } from "../agendaGroups";
import { importedSourceLabel, isImportedSource } from "../importedSources";

export type GridPerson = { id: string; name: string; color: string };

export type GridEvent = {
  key: string;
  title: string;
  start: string;
  end?: string | null;
  allDay: boolean;
  location?: string | null;
  personIds: string[];
  /** Person des eintragenden Benutzers (z. B. Julian trägt für Greta ein). */
  createdByPersonId?: string | null;
  source?: Schemas["ChangeSource"];
  onSelect?: () => void;
};

const UNASSIGNED = "__none__";

/** Kürzestes eindeutiges Kürzel je Person (A, K, G, J … bzw. mehr Buchstaben bei Gleichstand). */
export function shortNames(persons: GridPerson[]): Map<string, string> {
  const result = new Map<string, string>();
  for (const p of persons) {
    let len = 1;
    const name = p.name.trim();
    while (len < name.length && persons.some((o) => o.id !== p.id && o.name.trim().slice(0, len).toLowerCase() === name.slice(0, len).toLowerCase())) {
      len++;
    }
    result.set(p.id, name.slice(0, len).toUpperCase());
  }
  return result;
}

export function coversDay(e: GridEvent, day: string): boolean {
  const start = datePart(e.start);
  if (e.allDay) {
    const endExclusive = e.end ? datePart(e.end) : addDays(start, 1);
    return start <= day && day < endExclusive;
  }
  return start === day;
}

export function timeLabel(e: GridEvent): string {
  if (e.allDay) return "ganztägig";
  const s = timePart(e.start);
  const end = e.end ? timePart(e.end) : null;
  return end ? `${s}–${end}` : `${s} · offen`;
}

/**
 * Wochenraster mit einer Spalte je Person: jede Person ist über Name, Farbe und Kürzel eindeutig erkennbar
 * (Farbe ist nie die einzige Kennzeichnung). Termine für alle erscheinen als ein durchgehendes Band; Termine für
 * mehrere stehen in jeder beteiligten Spalte. „von X“ markiert Einträge, die jemand für andere angelegt hat.
 */
export function PersonWeekGrid({
  persons,
  events,
  from,
  days = 7,
  today,
  variant = "app",
  fill = false,
  corner,
  focusDay = null,
  focusToken = 0,
  ready = true,
  compactSchool = false,
  now,
}: {
  persons: GridPerson[];
  events: GridEvent[];
  from: string;
  days?: number;
  today: string;
  variant?: "app" | "display";
  /** Füllt die verfügbare Höhe: Tageszeilen teilen sich den Platz und wachsen mit vielen Terminen (dann scrollt das Raster). */
  fill?: boolean;
  /** Inhalt der linken oberen Ecke (z. B. Kalenderwoche). */
  corner?: ReactNode;
  /** Tag, der beim Öffnen sichtbar sein soll (heute bzw. gewählter Tag), falls das Raster scrollt. */
  focusDay?: string | null;
  focusToken?: number;
  /** Erst springen, wenn die Termine geladen sind (sonst ist die Seite noch zu kurz). */
  ready?: boolean;
  compactSchool?: boolean;
  now?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const box = scrollRef.current;
    if (!box) return;
    const row = focusDay ? box.querySelector<HTMLElement>(`[data-day="${focusDay}"]`) : null;
    const head = box.querySelector<HTMLElement>(".pgrid-person");
    box.style.setProperty("--pgrid-head", `${head?.offsetHeight ?? 0}px`);
    // Nur den eigenen Scrollbereich bewegen (nicht die Seite); Kopfzeile bleibt sichtbar.
    box.scrollTo?.({ top: row ? Math.max(0, row.offsetTop - (head?.offsetHeight ?? 0)) : 0 });
  }, [from, focusDay, focusToken, ready]);
  const short = shortNames(persons);
  const known = new Set(persons.map((p) => p.id));
  const isForAll = (e: GridEvent) => persons.length > 1 && persons.every((p) => e.personIds.includes(p.id));
  const hasUnassigned = events.some((e) => !e.personIds.some((id) => known.has(id)));
  const columns: GridPerson[] = hasUnassigned ? [...persons, { id: UNASSIGNED, name: "Ohne Person", color: "#8a96a3" }] : persons;
  const dayList = Array.from({ length: days }, (_, i) => addDays(from, i));
  const sorted = [...events].sort((a, b) => Number(b.allDay) - Number(a.allDay) || a.start.localeCompare(b.start));
  const creatorOf = (e: GridEvent) =>
    e.createdByPersonId && known.has(e.createdByPersonId) && !e.personIds.includes(e.createdByPersonId)
      ? persons.find((x) => x.id === e.createdByPersonId)
      : undefined;

  const card = (e: GridEvent, color: string, others: string[], extraClass = "") => {
    const creator = isImportedSource(e.source) ? undefined : creatorOf(e);
    return (
      <button
        key={e.key}
        type="button"
        className={`pgrid-event${e.allDay ? " allday" : ""}${timetableClasses(e.source, e.title)}${extraClass}`}
        data-source={e.source}
        style={{ borderLeftColor: `var(--event-accent, ${color})`, background: `var(--event-bg, color-mix(in srgb, ${color} 14%, var(--surface)))` }}
        onClick={e.onSelect}
        disabled={!e.onSelect}
        aria-label={`${e.title} · ${timeLabel(e)}`}
        title={[importedSourceLabel(e.source), e.title, timeLabel(e), e.location, creator ? `eingetragen von ${creator.name}` : null].filter(Boolean).join(" · ")}
      >
        <span className="t">{timeLabel(e)}</span>
        <span className="title" lang="de">{e.source === "web_untis" ? <><span className="school-full-title">{e.title}</span><span className="school-short-title" aria-hidden="true">{schoolShortTitle(e.title)}</span></> : e.title}</span>
        {timetableChange(e.source, e.title) && <span className="school-status">{timetableChange(e.source, e.title) === "cancelled" ? "Entfällt" : "Geändert"}</span>}
        {e.location && <span className="loc">{e.location}</span>}
        {creator && (
          <span className="by" aria-label={`eingetragen von ${creator.name}`}>
            von <span className="mini" style={{ background: creator.color }}>{short.get(creator.id)}</span>
          </span>
        )}
        {others.length > 0 && (
          <span className="with" aria-label={`gemeinsam mit ${others.map((id) => persons.find((x) => x.id === id)?.name).join(", ")}`}>
            <span className="with-label">mit</span>
            {others.map((id) => (
              <span key={id} className="mini" style={{ background: persons.find((x) => x.id === id)?.color }}>{short.get(id)}</span>
            ))}
          </span>
        )}
      </button>
    );
  };

  // auto-Zeilen teilen freien Platz gleich auf und wachsen mit dem Inhalt (fr-Zeilen würden sich alle nach der vollsten richten).
  const rows = dayList.map(() => "auto").join(" ");

  return (
    <div className={`pgrid-scroll pgrid-${variant}${fill ? " pgrid-fill" : ""}`} ref={scrollRef}>
      <div className="pgrid" style={{ "--pgrid-cols": columns.length, "--pgrid-rows": rows } as CSSProperties} role="table" aria-label={days === 1 ? "Tagesübersicht nach Personen" : "Wochenübersicht nach Personen"}>
        <div className="pgrid-corner" role="columnheader">{corner}</div>
        {columns.map((p) => (
          <div key={p.id} className="pgrid-person" role="columnheader" style={{ borderBottomColor: p.color }}>
            <span className="pgrid-badge" style={{ background: p.color }} aria-hidden="true">{p.id === UNASSIGNED ? "?" : short.get(p.id)}</span>
            <span className="pgrid-name">{p.name}</span>
          </div>
        ))}
        {dayList.map((day) => {
          const dayEvents = sorted.filter((e) => coversDay(e, day));
          const forAll = dayEvents.filter(isForAll);
          const rest = dayEvents.filter((e) => !isForAll(e));
          const banded = forAll.length > 0 && rest.length > 0;
          return (
            <div key={day} data-day={day} className={`pgrid-row${day === today ? " today" : day < today ? " past" : ""}${banded ? " banded" : ""}`} role="row">
              <div className="pgrid-day" role="rowheader" style={banded ? { gridRow: "span 2" } : undefined}>
                {/* Bleibt in hohen Zeilen beim Scrollen oben sichtbar. */}
                <span className="pgrid-day-label">
                  <span className="dow">{formatWeekday(day).slice(0, 2)}</span>
                  <span className="date">{formatShortDate(day).split(" ")[1]}</span>
                </span>
              </div>
              {forAll.length > 0 && (
                <div className="pgrid-band" role="cell" aria-label={`Alle, ${formatWeekday(day)}`}>
                  {forAll.map((e) => card(e, "var(--primary)", [], " for-all"))}
                </div>
              )}
              {(forAll.length === 0 || rest.length > 0) && columns.map((p) => {
                const cell = rest.filter((e) => (p.id === UNASSIGNED ? !e.personIds.some((id) => known.has(id)) : e.personIds.includes(p.id)));
                const finished = days === 1 && day === today && now ? cell.filter(e => e.source === "web_untis" && !timetableChange(e.source, e.title) && agendaPeriod(e, now) === "past") : [];
                const active = cell.filter(e => !finished.includes(e));
                const lessons = compactSchool ? active.filter(e => e.source === "web_untis" && !timetableChange(e.source, e.title)) : [];
                const visible = lessons.length > 1 ? active.filter(e => !lessons.includes(e)) : active;
                return (
                  <div key={p.id} className="pgrid-cell" role="cell" aria-label={`${p.name}, ${formatWeekday(day)}`}>
                    {lessons.length > 1 && <details className="school-bundle" key={`${p.id}-${day}`}>
                      <summary><strong>{lessons.length} Unterrichtsblöcke</strong><span>{timePart(lessons[0].start)}–{timePart(lessons.at(-1)!.end ?? lessons.at(-1)!.start)}</span></summary>
                      {lessons.map(e => card(e, p.color, []))}
                    </details>}
                    {visible.map((e) => card(e, p.color, e.personIds.filter((id) => id !== p.id && known.has(id))))}
                    {finished.length > 0 && <details className="past-events"><summary>Unterricht vorbei ({finished.length})</summary>{finished.map(e => card(e, p.color, []))}</details>}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
